/**
 * streak.ts -- Phase 50: Streak enemy module.
 *
 * Fast straight-line enemies with pre-launch warning chevron and fading damage trail.
 * Module-level state for pending warnings; streak enemies live in state.enemies[].
 * Pure functions consumed by GameCanvas.tsx.
 */

import type { Enemy, GameState, StreakWarning, WordPoolEntry } from './types';
import { getSpawnPosition, ENEMY_FONT, PILL_PAD_X } from './enemies';
import { activateWord } from './wordPool';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STREAK_SPEED = 350 / 60;           // CSS px/frame @ 60fps (~5.83)
const WARNING_DURATION = 60;              // frames, ~1s at 60fps
const TRAIL_LENGTH = 12;
const TRAIL_HIT_RADIUS = 20;             // CSS px
const OFF_CANVAS_BUFFER = 60;            // CSS px
const PILL_PAD_Y = 6;                    // match enemies.ts
const PILL_RADIUS = 8;                   // match enemies.ts
const FONT_SIZE = 14;                    // match enemies.ts
const AMBER = 'rgba(251, 191, 36';       // base color for warning + trail + border
const PILL_BG = 'rgba(20, 20, 20, 0.9)'; // same as homing
const HIGHLIGHT = '#c084fc';             // same as homing

// ---------------------------------------------------------------------------
// Module-level state (reset on game restart via resetStreak)
// ---------------------------------------------------------------------------

let pendingWarnings: StreakWarning[] = [];

// ---------------------------------------------------------------------------
// spawnStreakWarning
// ---------------------------------------------------------------------------

/**
 * Pick a word from state.wordPool and create a pending StreakWarning at a random edge.
 * vx/vy are pre-computed at warning time so the enemy launches on the same trajectory.
 */
export function spawnStreakWarning(state: GameState, cssW: number, cssH: number): void {
  if (state.wordPool.length === 0) return;

  const entry = state.wordPool.shift() as WordPoolEntry;

  // activateWord hides the source element; skip non-fallback words that return null.
  // Return the entry to the pool so it is not silently dropped on early-return.
  const rect = activateWord(entry);
  if (rect === null && entry.category !== 'fallback') {
    state.wordPool.unshift(entry);
    return;
  }

  const spawn = getSpawnPosition(cssW, cssH);

  // Base angle: inward from spawn edge (canvas coords: positive Y = down)
  let baseAngle: number;
  switch (spawn.edge) {
    case 'top':    baseAngle = Math.PI / 2;  break; // pointing down
    case 'right':  baseAngle = Math.PI;      break; // pointing left
    case 'bottom': baseAngle = -Math.PI / 2; break; // pointing up
    case 'left':   baseAngle = 0;            break; // pointing right
    default:       baseAngle = 0;
  }

  // ±15 degrees random jitter
  const angle = baseAngle + (Math.random() - 0.5) * (Math.PI / 6);
  const vx = Math.cos(angle) * STREAK_SPEED;
  const vy = Math.sin(angle) * STREAK_SPEED;

  pendingWarnings.push({
    edge: spawn.edge,
    edgeX: spawn.edgeX,
    edgeY: spawn.edgeY,
    spawnX: spawn.x,
    spawnY: spawn.y,
    vx,
    vy,
    timer: WARNING_DURATION,
    pulsePhase: 0,
    entry,
  });
}

// ---------------------------------------------------------------------------
// tickStreakWarnings
// ---------------------------------------------------------------------------

/**
 * Decrement warning timers. When timer <= 0, convert warning to a streak Enemy
 * in state.enemies and splice it from pendingWarnings.
 */
export function tickStreakWarnings(state: GameState, dt: number): void {
  for (let i = pendingWarnings.length - 1; i >= 0; i--) {
    const warning = pendingWarnings[i];
    warning.timer -= dt * 60;
    warning.pulsePhase += 0.12 * dt * 60;

    if (warning.timer <= 0) {
      const enemy: Enemy = {
        word: warning.entry.word,
        typed: 0,
        x: warning.spawnX,
        y: warning.spawnY,
        vx: warning.vx,
        vy: warning.vy,
        wobblePhase: 0,
        spawnCategory: warning.entry.category,
        fontSize: warning.entry.fontSize,
        type: 'streak',
        trail: [],
      };
      state.enemies.push(enemy);
      pendingWarnings.splice(i, 1);
    }
  }
}

// ---------------------------------------------------------------------------
// updateStreak
// ---------------------------------------------------------------------------

/**
 * Advance a streak enemy one frame: fixed straight-line movement + trail append.
 * Does NOT call computeSpeed() -- streak speed is locked at spawn time in vx/vy.
 */
export function updateStreak(enemy: Enemy, dt: number): void {
  const scale = dt * 60;
  enemy.x += enemy.vx * scale;
  enemy.y += enemy.vy * scale;

  if (!enemy.trail) enemy.trail = [];
  enemy.trail.push({ x: enemy.x, y: enemy.y });
  if (enemy.trail.length > TRAIL_LENGTH) {
    enemy.trail.splice(0, enemy.trail.length - TRAIL_LENGTH);
  }
}

// ---------------------------------------------------------------------------
// isStreakOffCanvas
// ---------------------------------------------------------------------------

/**
 * Return true when the enemy has fully exited the canvas plus OFF_CANVAS_BUFFER.
 * Caller is responsible for silent removal (no life decrement) per STREAK-05.
 */
export function isStreakOffCanvas(enemy: Enemy, cssW: number, cssH: number): boolean {
  return (
    enemy.x < -OFF_CANVAS_BUFFER ||
    enemy.x > cssW + OFF_CANVAS_BUFFER ||
    enemy.y < -OFF_CANVAS_BUFFER ||
    enemy.y > cssH + OFF_CANVAS_BUFFER
  );
}

// ---------------------------------------------------------------------------
// checkTrailCollision
// ---------------------------------------------------------------------------

/**
 * Return true if the player cursor is within TRAIL_HIT_RADIUS of any trail position
 * on any active streak enemy. Caller must apply the same clearAllPhase / isInsideSelection
 * guards as the existing pill collision block.
 */
export function checkTrailCollision(state: GameState): boolean {
  for (const enemy of state.enemies) {
    if (enemy.type !== 'streak') continue;
    if (!enemy.trail) continue;
    for (const pos of enemy.trail) {
      const dist = Math.hypot(pos.x - state.mouseX, pos.y - state.mouseY);
      if (dist < TRAIL_HIT_RADIUS) return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// drawStreakWarning
// ---------------------------------------------------------------------------

/**
 * Draw a pulsing amber chevron (V shape) at the warning's edge position pointing inward.
 * Chevron size: 12px arms, 8px depth.
 */
export function drawStreakWarning(ctx: CanvasRenderingContext2D, warning: StreakWarning): void {
  ctx.save();

  const pulse = 0.6 + 0.4 * (0.5 + 0.5 * Math.sin(warning.pulsePhase));
  ctx.strokeStyle = `${AMBER}, ${(0.9 * pulse).toFixed(3)})`;
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const ARM = 12; // px arm length
  const DEPTH = 8; // px inward depth

  const { edgeX: ex, edgeY: ey, edge } = warning;

  ctx.beginPath();
  switch (edge) {
    case 'top':
      // V pointing down from top edge
      ctx.moveTo(ex - ARM, ey);
      ctx.lineTo(ex,       ey + DEPTH);
      ctx.lineTo(ex + ARM, ey);
      break;
    case 'right':
      // V pointing left from right edge
      ctx.moveTo(ex,        ey - ARM);
      ctx.lineTo(ex - DEPTH, ey);
      ctx.lineTo(ex,        ey + ARM);
      break;
    case 'bottom':
      // V pointing up from bottom edge
      ctx.moveTo(ex - ARM, ey);
      ctx.lineTo(ex,       ey - DEPTH);
      ctx.lineTo(ex + ARM, ey);
      break;
    case 'left':
      // V pointing right from left edge
      ctx.moveTo(ex,        ey - ARM);
      ctx.lineTo(ex + DEPTH, ey);
      ctx.lineTo(ex,        ey + ARM);
      break;
  }
  ctx.stroke();

  ctx.restore();
}

// ---------------------------------------------------------------------------
// drawAllStreakWarnings
// ---------------------------------------------------------------------------

/**
 * Draw all pending streak warnings on the canvas.
 */
export function drawAllStreakWarnings(ctx: CanvasRenderingContext2D): void {
  for (const warning of pendingWarnings) {
    drawStreakWarning(ctx, warning);
  }
}

// ---------------------------------------------------------------------------
// drawStreakTrail
// ---------------------------------------------------------------------------

/**
 * Draw the fading amber trail behind a streak enemy.
 * Index 0 = oldest = opacity 0.0, index n-1 = newest (head-adjacent) = opacity 0.8.
 * Trail is drawn BEFORE the enemy pill so the pill renders on top.
 */
export function drawStreakTrail(ctx: CanvasRenderingContext2D, enemy: Enemy): void {
  if (!enemy.trail || enemy.trail.length === 0) return;
  ctx.save();

  const n = enemy.trail.length;
  for (let i = 0; i < n; i++) {
    const pos = enemy.trail[i];
    const opacity = (i / Math.max(n - 1, 1)) * 0.8;
    ctx.globalAlpha = opacity;
    ctx.fillStyle = `${AMBER}, 1)`;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 6, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// drawStreakEnemy
// ---------------------------------------------------------------------------

/**
 * Render a streak enemy pill: identical to drawEnemy but with amber border instead
 * of urgent-red border. Uses same dark background and highlight colors as homing enemies.
 */
export function drawStreakEnemy(ctx: CanvasRenderingContext2D, enemy: Enemy): void {
  ctx.save();
  ctx.font = ENEMY_FONT;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';

  const remaining = enemy.word.length - enemy.typed;
  const remainingText = enemy.word.slice(enemy.typed);
  const textW = ctx.measureText(remainingText).width;
  const pillW = textW + PILL_PAD_X * 2;
  const pillH = FONT_SIZE + PILL_PAD_Y * 2;
  const pillX = enemy.x - pillW / 2;
  const pillY = enemy.y - pillH / 2;

  // Pill background
  ctx.fillStyle = PILL_BG;
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(pillX, pillY, pillW, pillH, PILL_RADIUS);
  } else {
    ctx.rect(pillX, pillY, pillW, pillH);
  }
  ctx.fill();

  // Amber border (always shown for streak enemies to visually distinguish type)
  ctx.strokeStyle = `${AMBER}, 0.8)`;
  ctx.lineWidth = 2;
  ctx.stroke();

  // Render remaining (untyped) characters
  const textY = pillY + PILL_PAD_Y;
  let cursorX = pillX + PILL_PAD_X;

  for (let i = enemy.typed; i < enemy.word.length; i++) {
    const char = enemy.word[i];
    const charW = ctx.measureText(char).width;

    if (i === enemy.typed) {
      // Next character to type: use highlight (streak never goes urgent-red)
      ctx.fillStyle = remaining <= 2 ? '#ef4444' : HIGHLIGHT;
    } else {
      ctx.fillStyle = '#e0e0e0';
    }

    ctx.fillText(char, cursorX, textY);
    cursorX += charW;
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// getPendingWarningCount
// ---------------------------------------------------------------------------

/**
 * Return count of pending streak warnings (so GameCanvas can include in totalActive count).
 */
export function getPendingWarningCount(): number {
  return pendingWarnings.length;
}

// ---------------------------------------------------------------------------
// resetStreak
// ---------------------------------------------------------------------------

/**
 * Clear module-level pending warnings on game restart.
 * Streak enemies in state.enemies are cleared by resetGameInPlace's state.enemies.length = 0.
 */
export function resetStreak(): void {
  pendingWarnings = [];
}

